import React, { useState, useEffect, useCallback } from "react";
import {
  FaSpinner,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaFileInvoiceDollar,
  FaPrint,
} from "react-icons/fa";
import apiRequest from "../services/api";
import ProfitAndLossReport from "../utils/ProfitAndLossReport";

export default function IncomeStatementPage() {
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];
  const [currentUser, setCurrentUser] = useState(() => {
    const savedUser = localStorage.getItem("user");
    try {
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (e) {
      return null;
    }
  });

  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const currentYear = today.getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);

  const [endDate, setEndDate] = useState(
    `${currentYear}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`
  );

  const [data, setData] = useState({
    revenueRows: [],
    totalRevenue: 0,
    cogsRows: [],
    totalCogs: 0,
    grossProfit: 0,
    expenseRows: [],
    totalExpenses: 0,
    netIncome: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showPrint, setShowPrint] = useState(false);

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return `ETB ${num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append("startDate", `${startDate}T00:00:00`);
      }
      if (endDate) {
        params.append("endDate", `${endDate}T23:59:59`);
      }

      const res = await apiRequest(
        `reports/income-statement?${params.toString()}`
      );

      const payload = res?.data || res;

      setData({
        revenueRows: payload?.revenueRows || [],
        totalRevenue: payload?.totalRevenue || 0,
        cogsRows: payload?.cogsRows || [],
        totalCogs: payload?.totalCogs || 0,
        grossProfit: payload?.grossProfit || 0,
        expenseRows: payload?.expenseRows || [],
        totalExpenses: payload?.totalExpenses || 0,
        netIncome: payload?.netIncome || 0,
      });
    } catch (err) {
      setError(
        err?.message || "Failed to load income statement. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const periodLabel =
    startDate && endDate
      ? `${new Date(startDate).toLocaleDateString("en-GB")} – ${new Date(
          endDate
        ).toLocaleDateString("en-GB")}`
      : "";

  const inputClass =
    "w-full rounded-xl pl-10 pr-4 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const cardClass =
    "p-5 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl";

  // ========== PRINT VIEW ==========
  if (showPrint) {
    return (
      <ProfitAndLossReport
        data={data}
        companyName={currentUser.data?.tenant?.name}
        reportTitle="Profit & Loss Statement"
        periodLabel={periodLabel}
        onAfterPrint={() => setShowPrint(false)}
      />
    );
  }

  return (
    <div className="min-h-full relative overflow-hidden font-sans p-4 md:p-8 bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      {/* Background Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full pointer-events-none bg-blue-500/5 blur-[120px] dark:bg-blue-500/10" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full pointer-events-none bg-emerald-500/5 blur-[150px] dark:bg-emerald-500/10" />

      <div className="max-w-7xl mx-auto relative z-10 space-y-6">
        {/* HEADER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-6 rounded-2xl border backdrop-blur-xl bg-white border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
              <FaFileInvoiceDollar className="text-emerald-600 dark:text-emerald-400" />
              Profit & Loss
            </h1>
            <p className="text-xs mt-1 text-slate-500 dark:text-slate-400">
              Income Statement • Real-time accounting
            </p>
          </div>

          {/* PRINT BUTTON - right side */}
          <button
            onClick={() => setShowPrint(true)}
            disabled={loading || !!error}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold
                       bg-emerald-600 text-white hover:bg-emerald-700
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition shadow-sm"
          >
            <FaPrint />
            Print
          </button>
        </div>

        {/* DATE FILTERS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-2xl border backdrop-blur-md bg-white border-slate-200 shadow-sm dark:bg-slate-900/30 dark:border-white/10">
          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              Start Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400">
              End Date
            </label>
            <div className="relative flex items-center mt-1">
              <FaCalendarAlt className="absolute left-3 text-slate-400 dark:text-slate-500" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="text-center py-12 rounded-2xl border backdrop-blur-md bg-white border-slate-200 text-slate-500 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:text-slate-400">
            <FaSpinner className="animate-spin inline mr-3 text-emerald-500 text-lg" />
            Loading income statement...
          </div>
        )}

        {/* ERROR */}
        {error && (
          <div className="flex items-center gap-3 justify-center text-center py-8 rounded-2xl border bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {/* CONTENT */}
        {!loading && !error && (
          <div className={`${cardClass} space-y-6`}>
            {/* REVENUE */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest border-b pb-2 mb-3 text-emerald-600 border-slate-200 dark:text-emerald-400 dark:border-white/10">
                Revenues
              </h3>
              <div className="space-y-2">
                {data.revenueRows?.map((r) => (
                  <div
                    key={r.accountId}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="pr-4 text-slate-500 dark:text-slate-400">
                      {r.accountName}
                    </div>
                    <span className="font-mono font-medium text-right whitespace-nowrap text-slate-800 dark:text-slate-100">
                      {formatCurrency(r.amount)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-bold p-3 rounded-xl mt-4 text-base text-emerald-700 bg-emerald-50 border border-emerald-100 dark:text-emerald-300 dark:bg-emerald-500/[0.05] dark:border-emerald-500/10">
                <span>Total Revenue</span>
                <span className="font-mono">
                  {formatCurrency(data.totalRevenue)}
                </span>
              </div>
            </div>

            {/* COGS */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest border-b pb-2 mb-3 text-amber-600 border-slate-200 dark:text-amber-400 dark:border-white/10">
                Cost of Goods Sold (COGS)
              </h3>
              <div className="space-y-2">
                {data.cogsRows?.map((r) => (
                  <div
                    key={r.accountId}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="pr-4 text-slate-500 dark:text-slate-400">
                      {r.accountName}
                    </div>
                    <span className="font-mono font-medium text-right whitespace-nowrap text-red-600 dark:text-red-400">
                      ({formatCurrency(r.amount)})
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-bold p-3 rounded-xl mt-4 text-base text-amber-700 bg-amber-50 border border-amber-100 dark:text-amber-300 dark:bg-amber-500/[0.05] dark:border-amber-500/10">
                <span>Total Cost of Sales</span>
                <span className="font-mono">
                  ({formatCurrency(data.totalCogs)})
                </span>
              </div>
            </div>

            {/* GROSS MARGIN */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-2xl border border-blue-200 bg-blue-50 dark:border-blue-500/20 dark:bg-blue-500/[0.04]">
              <span className="font-extrabold text-lg text-blue-700 dark:text-blue-300">
                GROSS MARGIN
              </span>
              <span className="font-mono text-xl font-bold mt-1 sm:mt-0 text-blue-700 dark:text-blue-300">
                {formatCurrency(data.grossProfit)}
              </span>
            </div>

            {/* OPERATING EXPENSES */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest border-b pb-2 mb-3 text-purple-600 border-slate-200 dark:text-purple-400 dark:border-white/10">
                Operating Expenses
              </h3>
              <div className="space-y-2">
                {data.expenseRows?.map((r) => (
                  <div
                    key={r.accountId}
                    className="flex justify-between items-center text-sm"
                  >
                    <div className="pr-4 text-slate-500 dark:text-slate-400">
                      {r.accountName}
                    </div>
                    <span className="font-mono font-medium text-right whitespace-nowrap text-red-600 dark:text-red-400">
                      ({formatCurrency(r.amount)})
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between font-bold p-3 rounded-xl mt-4 text-base text-purple-700 bg-purple-50 border border-purple-100 dark:text-purple-300 dark:bg-purple-500/[0.05] dark:border-purple-500/10">
                <span>Total Operating Expenses</span>
                <span className="font-mono">
                  ({formatCurrency(data.totalExpenses)})
                </span>
              </div>
            </div>

            {/* NET INCOME */}
            <div
              className={`flex flex-col sm:flex-row justify-between items-start sm:items-center p-5 rounded-2xl border font-black text-lg ${
                data.netIncome >= 0
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/[0.05] dark:border-emerald-500/30 dark:text-emerald-300"
                  : "bg-red-50 border-red-200 text-red-700 dark:bg-red-500/[0.05] dark:border-red-500/30 dark:text-red-400"
              }`}
            >
              <span>NET INCOME / PROFIT</span>
              <span className="font-mono text-2xl mt-2 sm:mt-0">
                {formatCurrency(data.netIncome)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
